import { contract } from '@palaccount/contracts';
import { initClient, tsRestFetchApi, type ApiFetcherArgs } from '@ts-rest/core';

import { i18n } from '@/i18n';
import { currentViewAs, noteBlockedWrite } from '@/lib/view-as';

/**
 * G-08 — the one write the client lets through while a "View as" preview is
 * on. Signing out is never a tenant mutation, and the preview is cleared by it
 * (`useSignOut`), so refusing it would trap the Owner in the preview.
 */
const SIGN_OUT_PATH = '/api/auth/sign-out';

/** Thrown client-side, before any request leaves, for a write during a preview. */
export class ViewAsReadOnlyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ViewAsReadOnlyError';
  }
}

/**
 * The typed client, generated from the same contract the API implements
 * (DECISION.md §2.4). Adding a field to a response makes it available here
 * with no hand-written type and no chance of drift.
 *
 * `baseUrl` is empty because the SPA and the API are served from the same
 * origin (§5.3): the edge server serves the static build at `/` and proxies
 * `/api/*` to NestJS on the same hostname. That is what lets the session
 * cookie be `__Host-` prefixed with `SameSite=Strict`, leaves no CORS
 * configuration to get wrong, and makes `connect-src 'self'` sufficient.
 *
 * ── G-08: WRITES ARE REFUSED HERE WHILE "VIEW AS" IS ON ─────────────────────
 *
 * The preview is a browser-side projection of another person's permissions
 * over the Owner's OWN session (lib/view-as.ts). The server never learns a
 * preview exists, so a POST sent during one would be accepted and recorded as
 * the Owner's act while the banner says somebody else's name. The only honest
 * shape is to stop every non-GET before it leaves: the request is never made,
 * the caller gets a sentence naming the preview, and the banner repeats it.
 * GETs pass unchanged — they are what the preview is for.
 */
export const api = initClient(contract, {
  baseUrl: '',
  baseHeaders: {},
  // Sends the session cookie once Better Auth lands. Harmless today, and
  // getting it wrong later looks like an intermittent logout.
  credentials: 'same-origin',
  api: async (args: ApiFetcherArgs) => {
    const viewAs = currentViewAs();
    if (viewAs !== null && args.method !== 'GET' && !args.path.endsWith(SIGN_OUT_PATH)) {
      noteBlockedWrite();
      throw new ViewAsReadOnlyError(i18n.t('viewAs.readOnly', { name: viewAs.name }));
    }
    return tsRestFetchApi(args);
  },
});
